if (!customElements.get('fm-cart-recommendations')) {
  class FmCartRecommendations extends HTMLElement {
    static busy = false;

    constructor() {
      super();
      this.addEventListener('submit', (event) => this.addItem(event));
    }

    static surfaces() {
      const targets = new Map();
      for (const element of document.querySelectorAll('cart-items, cart-drawer-items')) {
        for (const section of element.getSectionsToRender()) {
          const root = document.getElementById(section.id);
          if (!root) continue;
          const target = root.querySelector(section.selector) || root;
          targets.set(`${section.id}:${section.selector}`, { ...section, target });
        }
      }
      const sections = [...new Set([
        'cart-drawer', 'cart-icon-bubble', 'cart-live-region-text',
        ...[...targets.values()].map((section) => section.section),
      ])];
      if (sections.length > 5) throw new Error('Cart refresh is unavailable. Please use the product page.');
      return { sections, targets: [...targets.values()] };
    }

    static prepareRender(payload, plan) {
      const parsed = new Map();
      for (const section of plan.sections) {
        const html = payload.sections?.[section];
        if (typeof html !== 'string' || !html.trim()) throw new Error('Incomplete cart refresh.');
        parsed.set(section, new DOMParser().parseFromString(html, 'text/html'));
      }
      return plan.targets.map((section) => {
        const source = parsed.get(section.section)?.querySelector(section.selector);
        if (!source || !section.target.isConnected) throw new Error('Cart refresh target unavailable.');
        return { target: section.target, html: source.innerHTML };
      });
    }

    standardEvent(variantId) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!CartLinesUpdateEvent) return null;
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add', context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }], promise: deferred.promise,
      }));
      return deferred;
    }

    resolveStandardEvent(deferred) {
      if (!deferred) return;
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      const request = typeof CartItems !== 'undefined'
        ? CartItems.fetchCartData()
        : fetch(`${routes.cart_url}.json`).then((response) => response.json());
      request.then((cart) => {
        if (!cart?.currency) throw new Error('Missing cart currency.');
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
      }).catch((error) => deferred.reject(error));
    }

    async addItem(event) {
      const form = event.target;
      if (!form.matches('.fm-cart-rec__form')) return;
      event.preventDefault();
      const errorNode = form.querySelector('.fm-cart-rec__error');
      const showError = (message) => { errorNode.textContent = message; errorNode.hidden = false; };
      if (FmCartRecommendations.busy) {
        showError('Another item is being added. Please try again in a moment.');
        return;
      }
      if (!form.reportValidity()) return;
      const formData = new FormData(form);
      const variantId = formData.get('id');
      if (!variantId) { showError('Choose a size first.'); return; }
      errorNode.hidden = true;
      FmCartRecommendations.busy = true;
      const buttons = [...document.querySelectorAll('fm-cart-recommendations button[type="submit"]')];
      buttons.forEach((button) => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      let accepted = false;
      let deferred;
      try {
        const plan = FmCartRecommendations.surfaces();
        const drawer = document.querySelector('cart-drawer');
        const fromDrawer = this.dataset.surface === 'drawer';
        deferred = this.standardEvent(variantId);
        formData.set('sections', plan.sections.join(','));
        formData.set('sections_url', window.location.pathname);
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;
        const response = await fetch(routes.cart_add_url, config);
        accepted = response.ok;
        const payload = await response.json();
        if (!response.ok || payload.status) {
          accepted = false;
          throw new Error(typeof payload.description === 'string' ? payload.description : 'This item could not be added. Please try another size.');
        }
        // Validate every requested section and target before changing any live DOM.
        const updates = FmCartRecommendations.prepareRender(payload, plan);
        const wasOpen = drawer?.classList.contains('active');
        if (wasOpen && typeof removeTrapFocus === 'function') removeTrapFocus();
        updates.forEach(({ target, html }) => { target.innerHTML = html; });
        document.querySelectorAll('cart-items, cart-drawer, #main-cart-footer').forEach((element) => element.classList.remove('is-empty'));
        if (drawer) {
          drawer.setActiveElement(document.getElementById('cart-icon-bubble'));
          drawer.classList.toggle('active', Boolean(wasOpen));
          const summary = drawer.querySelector('[id^="Details-"] summary');
          if (summary && !summary.hasAttribute('role')) drawer.setSummaryAccessibility(summary);
        }
        const newRowLink = (root) => root?.querySelector(`[data-quantity-variant-id="${variantId}"]`)?.closest('tr')?.querySelector('.cart-item__name');
        if (wasOpen) {
          const focus = newRowLink(drawer) || drawer.querySelector('#CartDrawer-Checkout');
          if (typeof trapFocus === 'function') trapFocus(drawer.querySelector('.drawer__inner'), focus);
          else focus?.focus();
        } else if (!fromDrawer) {
          (newRowLink(document.querySelector('cart-items')) || document.getElementById('checkout'))?.focus();
        }
        const summaryText = new DOMParser().parseFromString(payload.sections['cart-live-region-text'], 'text/html').body.textContent.trim();
        for (const id of ['cart-live-region-text', 'CartDrawer-LiveRegionText']) {
          const status = document.getElementById(id);
          if (status) {
            status.setAttribute('aria-live', 'polite');
            status.setAttribute('aria-hidden', 'false');
            status.textContent = `${payload.product_title || 'Item'} added. ${summaryText}`;
          }
        }
        this.resolveStandardEvent(deferred);
        // We rendered both surfaces. Native CartItems subscribers skip this source.
        if (typeof publish === 'function') {
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: payload, variantId })).catch(console.error);
        }
      } catch (error) {
        deferred?.reject(error);
        if (accepted) {
          // Add already succeeded: recover with a GET, never repeat the POST.
          window.location.assign(routes.cart_url);
          return;
        }
        showError(error.message || 'Could not confirm the add. Please review your cart before trying again.');
        const { CartErrorEvent } = window.StandardEvents || {};
        if (CartErrorEvent) this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'SERVICE_UNAVAILABLE' }));
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartError, { source: 'fm-cart-recommendations', productVariantId: variantId, errors: error.message });
      } finally {
        FmCartRecommendations.busy = false;
        buttons.forEach((button) => { button.disabled = false; button.removeAttribute('aria-busy'); });
      }
    }
  }
  customElements.define('fm-cart-recommendations', FmCartRecommendations);
}
